import { DeploymentCard, DeploymentCardSkeleton } from './DeploymentCard';
import { useDeploymentsContext } from '../context/DeploymentsContext';
import { useFillScrollableViewport } from '../hooks/useFillScrollableViewport';

import InfiniteScroll from '~/components/InfiniteScroll';

/** The element the listing scrolls inside — `Deployments` gives it this id. */
const SCROLLABLE_TARGET_ID = 'scrollable-content';

// The card fills its grid cell, so the track minimum is the card minimum. The design draws the
// card at 338px, which is enough for its English footer ("deployed", "17 hours ago") and not for
// the Portuguese one ("implantado", "17 horas atrás") — at 338px that line ran past the card and
// the problem indicator was clipped by the rounded corner. 362px fits the longest of the two, and
// costs no column: at every common viewport the row holds as many cards as it did at 338px.
function GridContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className='mb-10 grid grid-cols-[repeat(auto-fill,minmax(362px,1fr))] gap-6'>
      {children}
    </div>
  );
}

export function DeploymentsGridView() {
  const {
    loading,
    onInfinityScroll,
    deploymentsList,
    deploymentsData,
    menuActionHandlers
  } = useDeploymentsContext();
  const size = deploymentsList?.size || 0;
  const hasMore = !deploymentsList?.last;
  const isInitialLoading = loading && deploymentsData.length === 0;

  // A row of a wide grid holds more cards than a page brings, so the listing can end up with
  // nothing to scroll and no way to ask for the rest.
  useFillScrollableViewport({
    scrollableTargetId: SCROLLABLE_TARGET_ID,
    hasMore,
    loading,
    dataLength: deploymentsData.length,
    next: onInfinityScroll
  });

  if (isInitialLoading) {
    return (
      <GridContainer>
        {Array.from({ length: 10 }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <DeploymentCardSkeleton key={index} />
        ))}
      </GridContainer>
    );
  }

  return (
    <InfiniteScroll
      scrollableTarget={SCROLLABLE_TARGET_ID}
      dataLength={size || 0}
      pageStart={0}
      style={{ overflow: 'hidden' }}
      hasMore={hasMore}
      next={onInfinityScroll}
      data-testid='run-deployments-list'
      innerRef={() => {}}
      showLoader={false}
    >
      <GridContainer>
        {deploymentsData.map(({ deployment }) => (
          <DeploymentCard
            key={deployment.id}
            deployment={deployment}
            menuActionHandlers={menuActionHandlers}
          />
        ))}
        {loading &&
          hasMore &&
          deploymentsData.length > 0 &&
          Array.from({ length: 6 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <DeploymentCardSkeleton key={index} />
          ))}
      </GridContainer>
    </InfiniteScroll>
  );
}
